<script>
import i18next from 'i18next';
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { randomColor, sortPriceTargets } from '../../../helpers.js';
import Dropdown from '../../Dropdown.vue';
import NoItemFound from '../noItemFound.vue';

export default {
    name: 'PriceTargets',
    components: { Dropdown, NoItemFound },
    props: {
        heading: {
            type: String,
            required: true
        },
        size: {
            type: String,
            required: true
        },
        editMode: {
            type: Boolean,
            required: true,
            default: false
        }
    },
    setup(props) {
        const store = useStore();
        const editMode = ref(props.editMode);
        const priceTargets = ref([]);
        const priceTargetsFilterQuery = ref('');
        const priceTargetsSearchQuery = ref('');
        const isFilterDropdownOpen = ref(false);
        const priceTargetsStates = ref({});
        const loading = ref(true);

        const priceTargetsQuery = computed(() => {
            let filteredPriceTargets = sortPriceTargets(priceTargets.value);

            if (priceTargetsFilterQuery.value) {
                const filterQuery = priceTargetsFilterQuery.value.toLowerCase().split(' ');
                filteredPriceTargets = filteredPriceTargets.filter((item) => {
                    return filterQuery.every((v) => item.state.toLowerCase().includes(v));
                });
            }

            if (priceTargetsSearchQuery.value) {
                const searchQuery = priceTargetsSearchQuery.value.toLowerCase().split(' ');
                filteredPriceTargets = filteredPriceTargets.filter((item) => {
                    return searchQuery.every((v) => item.market_symbol.name.toLowerCase().includes(v));
                });
            }

            return filteredPriceTargets;
        });

        const storePriceTargets = computed(() => store.getters['price_targets/get']?.data?.data || []);

        // get the price targets from the store
        const getPriceTargets = async () => {
            if (Object.keys(storePriceTargets.value)?.length === 0) {
                await store.dispatch('price_targets/index');
                priceTargets.value = store.getters['price_targets/get']?.data?.data || [];
            }

            priceTargets.value = sortPriceTargets(
                storePriceTargets.value.map((target) => ({
                    ...target,
                    link: null
                }))
            );
            loading.value = false;
        };
        // Watcher to observe changes in the store's price targets
        watch(storePriceTargets, () => {
            getPriceTargets();
        });
        // get the filter states for filtering the price targets
        const getPriceTargetsStates = async () => {
            priceTargetsStates.value = store.getters['price_targets/getFilterStates'] || {};

            if (Object.keys(priceTargetsStates.value).length === 0) {
                await store.dispatch('price_targets/fetchFilterStates');
                priceTargetsStates.value = store.getters['price_targets/getFilterStates'];
            }
        };
        // Fetch the price targets and filter states on component mount
        onMounted(async () => {
            await getPriceTargetsStates();
            await getPriceTargets();
        });
        // Function to get the correct pricetarget level title based on the price target level
        const getLevelTitle = (priceTargetLevel) => {
            if (priceTargetLevel === 1) {
                return i18next.t('price_target');
            } else {
                return i18next.t('trading_area');
            }
        };
        // Function to get the correct pricetarget level description based on the price target level
        const getLevelDescription = (priceTargetlevel) => {
            if (priceTargetlevel === 1) {
                return i18next.t('price_target_description');
            } else {
                return i18next.t('trading_area_description');
            }
        };
        // Function to get the correct state title based on the price target state
        const getStateTitle = (priceTargetState) => {
            const state = Object.keys(priceTargetsStates.value).find((state) => state === priceTargetState);

            if (state) {
                return priceTargetsStates.value[state];
            }
        };
        // Function to get the correct pricetarget state description based on the price target state and level
        const getStateDescription = (priceTargetState) => {
            // Switch case to handle different states and return their corresponding descriptions
            switch (priceTargetState) {
                case 'In der Zielzone':
                    i18next.t('in_the_range_description');
                case 'Auf Zielzone zulaufend':
                    return i18next.t('active_description');
                case 'Ziel erreicht (Beendet)':
                case 'Erfolgreich':
                    return i18next.t('finished_description');
                case 'Alternativszenario':
                case 'Alternative':
                    return i18next.t('alternative_description');
                case 'Verworfen':
                    return i18next.t('discarded_description');
                case 'Gescheitert':
                    return i18next.t('failed_description');
                default:
                    return priceTargetState;
            }
        };

        /**
         * Function to navigate to the analysis page for a specific price target.
         *
         * @param {Object} target - The target object containing the recent_contentstream property.
         */
        const createLink = (target) => {
            const id = target?.recent_contentstream;

            if (id) {
                return `dashboard/markets-analysis/${id}`;
            }

            return 'dashboard';
        };

        /**
         * @param position string (top|bottom)
         * @param min number
         * @param max number
         * @returns string
         */
        const getTopBottomCoordinates = (position, min, max) => {
            const coordinateFactory = {
                top: () =>
                    Math.max(min, max)
                        .toFixed(2)
                        .toString()
                        .replace('.', ',')
                        .replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
                bottom: () =>
                    Math.min(min, max)
                        .toFixed(2)
                        .toString()
                        .replace('.', ',')
                        .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
            };

            return coordinateFactory[position] ? coordinateFactory[position]() : null;
        };

        /**
         * Function to get dynamic Tailwind classes based on the target's state and direction.
         *
         * @param {Object} target - The target object containing state and direction properties.
         * @returns {string} - The Tailwind classes to be applied based on the target's state and direction.
         */
        const dynamicPositionClasses = (target) => {
            if (target.state === 'Alternativszenario') {
                return 'bg-gray-300 text-white';
            }

            if (target.direction === 'Short') {
                return 'bg-dashboard-lightRed text-dashboard-lightRed';
            }

            return 'bg-dashboard-lightGreen text-dashboard-lightGreen';
        };

        // Return reactive data and computed properties
        return {
            loading,
            priceTargets,
            priceTargetsFilterQuery,
            priceTargetsSearchQuery,
            priceTargetsQuery,
            priceTargetsStates,
            isFilterDropdownOpen,
            editMode,
            dynamicPositionClasses,
            getStateTitle,
            getStateDescription,
            getLevelTitle,
            getLevelDescription,
            getTopBottomCoordinates,
            randomColor,
            createLink
        };
    }
};
</script>

<template>
    <div v-if="size === 'large'" class="bg-white rounded-xl tourStep-2">
        <div class="p-10">
            <div class="flex flex-row justify-between items-center bg-white pb-10 gap-6">
                <!-- Header section -->
                <div class="flex flex-span items-center gap-2 w-full">
                    <p class="bg-white text-lg font-bold text-black">{{ heading }}</p>
                    <info-text :class="{ invisible: editMode }" :title="heading" :text="$t('target_zones_trading_range_diffrences')" />
                </div>

                <!-- Search and Filter -->
                <div class="w-full">
                    <div class="flex flex-row justify-end gap-4 w-full z-[15]">
                        <div class="relative flex items-center min-w-[250px]">
                            <input
                                v-model="priceTargetsSearchQuery"
                                type="search"
                                name="zielzonen"
                                :placeholder="$t('plain_search')"
                                :label="$t('plain_search')"
                                :input-use="'search'"
                                class="block w-full px-3 py-2 border rounded-lg bg-dashboard-primary shadow-sm hover:shadow-md focus:outline-none text-xs pr-10"
                                :class="{ 'text-transparent': editMode, 'placeholder-transparent': editMode }"
                                @input="priceTargetsSearchQuery = $event.target.value"
                            />
                            <span class="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
                                <i class="fi fi-rr-search"></i>
                            </span>
                        </div>
                        <Dropdown
                            :dropdown-values="Object.entries(priceTargetsStates).map(([key, value]) => ({ key, title: $translate(value) }))"
                            :selected-dropdown-value="priceTargetsFilterQuery"
                            :placeholder="$t('all_zones')"
                            :disabled="editMode"
                            @update:selected-dropdown-value="(val) => (priceTargetsFilterQuery = val)"
                        />
                    </div>
                </div>
            </div>

            <!--  Pricetargets Table -->
            <Loading :loading="loading">
                <div class="h-[500px]" :class="editMode ? 'overflow-hidden' : 'overflow-y-auto overflow-x-none'">
                    <table class="table-auto text-left text-xxs text-dashboard-lightGray w-full relative flex-auto">
                        <thead
                            class="sticky top-0 text-black my-10 z-[14]"
                            :class="{ 'pointer-events-none': editMode }"
                            :style="{ background: 'linear-gradient(to bottom, rgba(255,255,255,1) 50%, rgba(255,255,255,0) 100%)' }"
                        >
                            <tr>
                                <th class="p-2 pb-10" :class="editMode ? 'rounded-xl text-center' : 'flex flex-span gap-2'">
                                    <span :class="editMode ? 'bg-dashboard-primary-light rounded-xl text-center p-2 text-transparent' : 'uppercase'">{{ $t('plain_values') }}</span>
                                </th>
                                <th class="p-2 pb-10 text-center">
                                    <span :class="editMode ? 'bg-dashboard-primary-light rounded-xl text-center p-2 text-transparent' : 'uppercase'">{{ $t('plain_type') }}</span>
                                </th>
                                <th class="p-2 pb-10 text-center">
                                    <span :class="editMode ? 'bg-dashboard-primary-light rounded-xl text-center p-2 text-transparent' : 'uppercase'">{{ $t('plain_state') }}</span>
                                </th>
                                <th class="p-2 pb-10" :class="editMode ? 'text-center' : ''">
                                    <div :class="editMode ? '' : 'flex items-center gap-2 justify-center'">
                                        <span :class="editMode ? 'bg-dashboard-primary-light rounded-xl text-center p-2 text-transparent' : 'uppercase'">
                                            {{ editMode ? '' : $t('plain_position') }}
                                        </span>
                                        <info-text v-if="!editMode" :title="$t('plain_position')" :text="$t('position_info')" />
                                    </div>
                                </th>
                                <th class="p-2 pb-10" :class="editMode ? 'text-center' : ''">
                                    <div :class="editMode ? '' : 'flex items-center gap-2 justify-center'">
                                        <span :class="editMode ? 'bg-dashboard-primary-light rounded-xl text-center p-2 text-transparent' : 'uppercase'">
                                            {{ editMode ? '' : $t('plain_coordinates') }}
                                        </span>
                                        <info-text v-if="!editMode" :title="$t('plain_coordinates')" :text="$t('coordinates_info')" />
                                    </div>
                                </th>
                                <th class="p-2 pb-10 text-center">
                                    <span :class="editMode ? 'bg-dashboard-primary-light rounded-xl text-center p-2 text-transparent' : 'uppercase'">{{ $t('plain_price') }}</span>
                                </th>
                                <th class="p-2 pb-10 text-center">
                                    <span :class="editMode ? 'bg-dashboard-primary-light rounded-xl text-center p-2 text-transparent' : 'uppercase'">{{
                                        $t('plain_analysis')
                                    }}</span>
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr
                                v-for="(target, index) in priceTargetsQuery"
                                :key="target.id"
                                :class="index % 2 === 0 ? 'bg-dashboard-primary-light' : ''"
                                :style="{ opacity: editMode ? 0.6 : 1 }"
                            >
                                <td class="py-4 pl-4 rounded-l-xl">
                                    <div class="flex flex-row gap-2 items-center">
                                        <div v-if="target.market_symbol.logo" class="flex-shrink-0">
                                            <img v-if="!editMode" class="w-10 h-10 rounded-xl object-contain" :src="target.market_symbol.logo" />
                                        </div>
                                        <div v-else :style="{ backgroundColor: randomColor() }" class="flex items-center justify-center rounded-xl w-10 h-10">
                                            <i v-if="!editMode" class="fi fi-rr-apartment text-white text-base"></i>
                                        </div>
                                        <div class="flex flex-col">
                                            <span v-if="!editMode" class="p-1 align-bottom pb-0 font-semibold text-black" style="line-height: 1.2">
                                                {{ target.market_symbol.name }}
                                            </span>
                                            <div v-if="editMode" class="bg-dashboard-primary-light w-20 h-6 rounded-xl"></div>
                                        </div>
                                    </div>
                                </td>
                                <td class="text-dashboard-primary-grey text-center grid-span-2 gap-1 justify-center">
                                    <div v-if="!editMode" class="inline-block align-middle">
                                        <span>{{ getLevelTitle(target.level) }}</span>
                                        <info-text :title="getLevelTitle(target.level)" :text="getLevelDescription(target.level)" class="inline-block ml-1" />
                                    </div>
                                    <div v-if="editMode" class="bg-dashboard-primary-light w-20 h-6 rounded-xl"></div>
                                </td>
                                <td class="text-dashboard-primary-grey text-center grid-span-2 gap-1 justify-center">
                                    <div v-if="!editMode" class="inline-block align-middle">
                                        <span>{{ $translate(getStateTitle(target.state)) }}</span>
                                        <info-text :title="$translate(getStateTitle(target.state))" :text="getStateDescription(target.state)" class="inline-block ml-1" />
                                    </div>
                                    <div v-if="editMode" class="bg-dashboard-primary-light w-20 h-6 rounded-xl"></div>
                                </td>
                                <td class="text-center">
                                    <div class="flex items-center justify-center h-full w-full">
                                        <span
                                            v-if="!editMode"
                                            :class="dynamicPositionClasses(target)"
                                            class="font-bold px-2 rounded-xl w-20 h-6 flex items-center justify-center uppercase"
                                        >
                                            {{ target.direction }}
                                        </span>
                                        <div v-if="editMode" class="bg-dashboard-primary-light w-20 h-6 rounded-xl"></div>
                                    </div>
                                </td>
                                <td class="text-dashboard-primary-grey py-2">
                                    <div class="flex items-center justify-center">
                                        <span v-if="!editMode" class="flex flex-col items-center">
                                            <span class="flex items-center gap-1 text-dashboard-lightGreen font-bold">
                                                <i class="fi fi-rr-arrow-alt-to-top"></i>
                                                <span class="ml-1">{{ getTopBottomCoordinates('top', target.min, target.max) }}</span>
                                            </span>
                                            <span class="flex items-center gap-1 text-dashboard-lightRed font-bold">
                                                <i class="fi fi-rr-arrow-alt-to-bottom"></i>
                                                <span class="ml-1">{{ getTopBottomCoordinates('bottom', target.min, target.max) }}</span>
                                            </span>
                                        </span>
                                        <div v-if="editMode" class="bg-dashboard-primary-light w-20 h-6 rounded-xl"></div>
                                    </div>
                                </td>
                                <td class="text-dashboard-primary-grey py-2 text-center">
                                    <span v-if="!editMode">{{
                                        target.market_symbol.last_price
                                            ?.toFixed(2)
                                            .toString()
                                            .replace('.', ',')
                                            .replace(/\B(?=(\d{3})+(?!\d))/g, '.') ?? '-'
                                    }}</span>
                                    <div v-if="editMode" class="bg-dashboard-primary-light w-20 h-6 rounded-xl"></div>
                                </td>
                                <td
                                    class="cursor-pointer text-center px-2 duration-300 transition z-1 rounded-r-xl text-black hover:opacity-100 bg-blue-light opacity-60"
                                    :class="{ 'pointer-events-none': editMode }"
                                >
                                    <router-link :to="createLink(target)" class="p-2 rounded-xl h-10 w-10" :class="{ 'opacity-50': editMode }">
                                        <i class="fi fi-rs-arrow-right text-lg text-dashboard-primary-blue" :class="{ invisible: editMode }"></i>
                                    </router-link>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-if="priceTargetsQuery.length === 0 && !editMode">
                        <no-item-found class="h-[400px]" :item-name="heading" />
                    </div>
                </div>
            </Loading>
        </div>
    </div>
    <!--Mobile PriceTargets-->
    <div v-if="size === 'small'">
        <div class="flex flex-col h-[600px]">
            <div class="rounded-xl bg-white flex flex-col gap-2 flex-1">
                <!-- Header section -->
                <div class="flex flex-span justify-start items-center gap-2 px-6 pt-6">
                    <p class="bg-white pb-1" :class="size === 'large' ? 'text-lg font-bold text-black' : 'text-base font-bold'">{{ heading }}</p>
                    <info-text v-if="!editMode" :title="heading" text="target_zones_trading_range_diffrences" />
                </div>

                <!-- Search and Filter -->
                <div class="w-full">
                    <div class="flex z-[15] gap-3 w-full px-6" :class="size === 'large' ? ' flex-row ' : 'flex-col'">
                        <div class="relative flex items-center min-w-[250px]" :class="({ 'pointer-events-none': editMode }, { group: size === 'large' })">
                            <!-- Search -->
                            <input
                                v-model="priceTargetsSearchQuery"
                                type="search"
                                name="zielzonen"
                                :placeholder="$t('plain_search')"
                                :label="$t('plain_search')"
                                :input-use="'search'"
                                class="block w-full px-3 py-2 border rounded-lg bg-dashboard-primary duration-300 shadow-sm hover:shadow-md focus:outline-none text-xs pr-10"
                                :class="{ 'text-transparent': editMode, 'placeholder-transparent': editMode }"
                            />
                            <!-- Search-Icon -->
                            <span class="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
                                <i class="fi fi-rr-search"></i>
                            </span>
                        </div>
                        <Dropdown
                            :dropdown-values="Object.entries(priceTargetsStates).map(([key, value]) => ({ key, title: $translate(value) }))"
                            :selected-dropdown-value="priceTargetsFilterQuery"
                            :placeholder="$t('all_zones')"
                            :disabled="editMode"
                            @update:selected-dropdown-value="(val) => (priceTargetsFilterQuery = val)"
                        />
                    </div>
                </div>

                <!-- PriceTargets Table-->
                <div
                    class="flex flex-col gap-3 px-6 mb-6 h-full text-xxs w-full justify-center"
                    :class="editMode ? 'overflow-hidden pointer-events-none' : 'overflow-y-auto overflow-x-hidden'"
                    :style="{ opacity: editMode ? 0.8 : 1 }"
                >
                    <div>
                        <div class="max-h-[400px]">
                            <table class="table-auto text-left text-xxs w-full relative pb-8">
                                <thead
                                    class="sticky top-0 text-black my-10 z-[14]"
                                    :class="{ 'pointer-events-none': editMode }"
                                    :style="{ background: 'linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)' }"
                                ></thead>
                                <tbody class="p-2 sm:p-4">
                                    <tr v-for="(target, index) in priceTargetsQuery" :key="index" class="text-xxs" :style="{ opacity: editMode ? 0.5 : 1 }">
                                        <td>
                                            <div class="rounded-xl p-4 bg-[#F1F6FA] mb-2 justify-between">
                                                <!-- Display target information -->
                                                <div class="flex justify-between items-center pb-2">
                                                    <!-- Icon, name, Level & State  -->
                                                    <div class="flex items-center space-x-2">
                                                        <div v-if="target.market_symbol.logo && !editMode" class="flex-shrink-0">
                                                            <img class="w-10 h-10 rounded-xl object-contain" :src="target.market_symbol.logo" />
                                                        </div>
                                                        <div v-else :style="{ backgroundColor: randomColor() }" class="flex items-center justify-center rounded-xl w-10 h-10">
                                                            <i v-if="!editMode" class="fi fi-rr-apartment text-white text-base"></i>
                                                        </div>
                                                        <div>
                                                            <span class="font-semibold" :class="editMode ? 'text-transparent bg-white rounded-xl' : ''">{{
                                                                target.market_symbol.name
                                                            }}</span>
                                                            <br />
                                                            <span
                                                                class="text-xxxs inline-flex"
                                                                :class="editMode ? 'text-transparent bg-white rounded-xl' : 'text-dashboard-lightGray'"
                                                            >
                                                                {{ getLevelTitle(target.level) }} ･ {{ $translate(getStateTitle(target.state)) }}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <!-- Direction -->
                                                    <span
                                                        class="font-bold"
                                                        :class="[
                                                            { invisible: editMode },
                                                            target.direction === 'Short'
                                                                ? 'bg-dashboard-lightRed text-dashboard-lightRed'
                                                                : 'bg-dashboard-lightGreen text-dashboard-lightGreen',
                                                            'uppercase p-2 rounded-xl px-4 py-1'
                                                        ]"
                                                    >
                                                        {{ target.direction }}
                                                    </span>
                                                </div>
                                                <hr class="my-1 bg-dashboard-primary-light" />

                                                <div
                                                    class="flex flex-col md:flex-row lg:flex-col items-center text-xxxs gap-1 justify-between pb-2"
                                                    :class="editMode ? 'text-transparent bg-white rounded-xl' : 'text-dashboard-lightGray'"
                                                >
                                                    <!-- Top coordinate -->
                                                    <span
                                                        class="w-full lg:w-full md:w-auto lg:justify-between justify-between md:justify-center font-bold gap-1 text-dashboard-lightGreen flex px-12 md:px-0 lg:px-12"
                                                        :class="editMode ? 'text-transparent bg-white rounded-xl' : 'text-dashboard-lightGreen'"
                                                    >
                                                        <span>
                                                            <i class="fi fi-rr-arrow-alt-to-top mr-1"></i>
                                                            {{ $t('plain_top') }}:
                                                        </span>
                                                        <span>{{ getTopBottomCoordinates('top', target.min, target.max) }}</span>
                                                    </span>
                                                    <!-- Bottom coordinate -->
                                                    <span
                                                        class="w-full lg:w-full md:w-auto justify-between font-bold gap-1 text-dashboard-lightGreen flex px-12 md:px-0 lg:px-12"
                                                        :class="editMode ? 'text-transparent bg-white rounded-xl' : 'text-dashboard-lightRed'"
                                                    >
                                                        <span>
                                                            <i class="fi fi-rr-arrow-alt-to-bottom mr-1"></i>
                                                            {{ $t('plain_bottom') }}:
                                                        </span>
                                                        {{ getTopBottomCoordinates('bottom', target.min, target.max) }}
                                                    </span>
                                                    <!-- Price -->
                                                    <span
                                                        class="w-full lg:w-full md:w-auto justify-between lg:justify-between md:justify-center font-bold gap-1 text-dashboard-lightGreen flex md:block px-12 md:px-0 lg:px-12"
                                                        :class="editMode ? 'text-transparent bg-white rounded-xl' : 'text-dashboard-primary-grey'"
                                                    >
                                                        <span>
                                                            <i class="fi fi-rr-arrow-trend-up text-xs mr-1"></i>
                                                            {{ $t('plain_price') }}:
                                                        </span>
                                                        {{
                                                            target.market_symbol.last_price
                                                                ?.toFixed(2)
                                                                .toString()
                                                                .replace('.', ',')
                                                                .replace(/\B(?=(\d{3})+(?!\d))/g, '.') ?? '-'
                                                        }}
                                                    </span>
                                                    <!-- Analysis button -->
                                                    <div
                                                        class="cursor-pointer duration-300 transition z-1 rounded-xl hover:opacity-100 bg-blue-light opacity-60 hidden md:block lg:hidden"
                                                        :class="{ 'pointer-events-none': editMode }"
                                                    >
                                                        <router-link
                                                            :to="createLink(target)"
                                                            class="cursor-pointer p-2 rounded-xl text-xxs font-bold justify-center self-center block"
                                                            :class="{ 'opacity-50': editMode }"
                                                        >
                                                            <span
                                                                class="flex items-center font-bold gap-1"
                                                                :class="editMode ? 'text-transparent bg-white rounded-xl' : 'text-dashboard-primary-blue'"
                                                            >
                                                                {{ $t('plain_analysis') }}
                                                                <i class="fi fi-rs-arrow-right text-xs mt-1"></i>
                                                            </span>
                                                        </router-link>
                                                    </div>
                                                </div>
                                                <div
                                                    class="cursor-pointer duration-300 transition z-1 rounded-xl hover:opacity-100 bg-blue-light opacity-60 text-center md:hidden lg:block py-1"
                                                    :class="{ 'pointer-events-none': editMode }"
                                                >
                                                    <router-link
                                                        :to="createLink(target)"
                                                        class="cursor-pointer rounded-xl p-2 text-xxs font-bold justify-center self-center"
                                                        :class="{ 'opacity-50': editMode }"
                                                    >
                                                        <span
                                                            class="flex items-center font-bold gap-1 h-5"
                                                            :class="editMode ? 'text-transparent bg-white rounded-xl' : 'text-dashboard-primary-blue'"
                                                        >
                                                            {{ $t('plain_analysis') }}
                                                            <i class="fi fi-rs-arrow-right text-xs mt-1"></i>
                                                        </span>
                                                    </router-link>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <div v-if="priceTargetsQuery.length == 0 && !editMode">
                                <no-item-found :item-name="$t('price_target')" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
